// Typed client for per-strategy paper accounts:
//   GET    /api/PaperAccount              — list the caller's accounts
//   GET    /api/PaperAccount/summaries    — live rollup per account (equity, P&L, bindings)
//   GET    /api/PaperAccount/{id}         — one account
//   PUT    /api/PaperAccount/{id}         — create or update
//   DELETE /api/PaperAccount/{id}         — delete (409 while a strategy is still bound)
//
// A paper account is a named pot of capital a strategy binds to, so two strategies stop
// sharing one balance — and one buying-power circuit breaker.

import { API_BASE } from '@/env';
import { apiFetch } from '@/auth/apiClient';

export type PaperAccountMode = 'Simulated' | 'Broker';

export interface PaperAccount {
  accountId: string;
  name: string;
  mode: PaperAccountMode;
  startingEquity: number;
  assetClass: string;
  provider: string;
  apiKey: string;
  /** The raw secret is never returned — only whether one is stored. */
  hasApiSecret: boolean;
  enabled: boolean;
  maxExposureMultiple: number;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertPaperAccountRequest {
  name: string;
  mode: PaperAccountMode;
  startingEquity: number;
  assetClass: string;
  provider?: string;
  apiKey?: string;
  /** Leave blank to keep the stored secret — the API never round-trips it. */
  apiSecret?: string;
  enabled: boolean;
  maxExposureMultiple: number;
}

/** One strategy bound to an account, as listed on its summary. */
export interface BoundStrategySummary {
  id: string;
  name: string;
  enabled: boolean;
}

/** Live rollup of one account: baseline plus what trading has done to it. P&L in USD. */
export interface PaperAccountSummary {
  accountId: string;
  name: string;
  mode: PaperAccountMode;
  assetClass: string;
  enabled: boolean;
  startingEquity: number;
  realizedPnL: number;
  unrealizedPnL: number;
  equity: number;
  openTradeCount: number;
  openExposure: number;
  /** Unbound strategies list under the shared default pot, where their trades land. */
  strategies: BoundStrategySummary[];
}

/** Thrown when a delete is refused because strategies are still bound to the account. */
export class PaperAccountInUseError extends Error {
  constructor(message: string, public readonly strategies: string[]) {
    super(message);
    this.name = 'PaperAccountInUseError';
  }
}

async function readError(res: Response): Promise<string> {
  try {
    const body = await res.json();
    return body?.error ?? `Request failed (${res.status})`;
  } catch {
    return `Request failed (${res.status})`;
  }
}

export const paperAccountService = {
  list: async (): Promise<PaperAccount[]> => {
    const res = await apiFetch(`${API_BASE}/api/PaperAccount`);
    if (!res.ok) throw new Error(await readError(res));
    return res.json() as Promise<PaperAccount[]>;
  },

  summaries: async (): Promise<PaperAccountSummary[]> => {
    const res = await apiFetch(`${API_BASE}/api/PaperAccount/summaries`);
    if (!res.ok) throw new Error(await readError(res));
    return res.json() as Promise<PaperAccountSummary[]>;
  },

  get: async (accountId: string): Promise<PaperAccount> => {
    const res = await apiFetch(`${API_BASE}/api/PaperAccount/${encodeURIComponent(accountId)}`);
    if (!res.ok) throw new Error(await readError(res));
    return res.json() as Promise<PaperAccount>;
  },

  upsert: async (accountId: string, req: UpsertPaperAccountRequest): Promise<PaperAccount> => {
    const res = await apiFetch(`${API_BASE}/api/PaperAccount/${encodeURIComponent(accountId)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(req),
    });
    if (!res.ok) throw new Error(await readError(res));
    return res.json() as Promise<PaperAccount>;
  },

  remove: async (accountId: string): Promise<void> => {
    const res = await apiFetch(`${API_BASE}/api/PaperAccount/${encodeURIComponent(accountId)}`, {
      method: 'DELETE',
    });
    if (res.ok) return;

    // 409 carries the strategies still bound to the account — surface them so the user can
    // act instead of guessing which strategy is holding the account open.
    if (res.status === 409) {
      try {
        const body = await res.json();
        throw new PaperAccountInUseError(
          body?.error ?? 'Account is still in use.',
          body?.strategies ?? []);
      } catch (e) {
        if (e instanceof PaperAccountInUseError) throw e;
        throw new Error('Account is still in use.');
      }
    }
    throw new Error(await readError(res));
  },
};
